import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Alert } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BuildIcon from '@mui/icons-material/Build';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { RecordDetailDrawer } from '../../components/common/RecordDetailDrawer';
import { masterDataAPI } from '../../services/api';
import { ModuleColumn, ModuleField } from '../../types';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { CORE_MODULE_KEYS } from '../../config/sidebarMenu';
import { useAuth } from '../../contexts/AuthContext';
import { useTableColumns } from '../../hooks/useTableColumns';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';
import { useSystemTotal } from '../../hooks/useSystemTotal';

type RefSlug = 'types' | 'makes' | 'models';

const REF_CONFIG: Record<RefSlug, {
  title: string;
  moduleKey: string;
  category: string;
  icon: React.ReactNode;
}> = {
  types: {
    title: 'Vehicle Types',
    moduleKey: 'vehicle_types',
    category: 'vehicle_type',
    icon: <LocalShippingIcon />,
  },
  makes: {
    title: 'Vehicle Makes',
    moduleKey: 'vehicle_makes',
    category: 'vehicle_make',
    icon: <BuildIcon />,
  },
  models: {
    title: 'Vehicle Models',
    moduleKey: 'vehicle_models',
    category: 'vehicle_model',
    icon: <DirectionsCarIcon />,
  },
};

export const FleetReferencePage: React.FC = () => {
  const { pathname } = useLocation();
  const { hasPermission } = useAuth();
  const ref: RefSlug = pathname.includes('/makes') ? 'makes' : pathname.includes('/models') ? 'models' : 'types';
  const config = REF_CONFIG[ref];
  const { total: systemTotal } = useSystemTotal(config.moduleKey);

  const canView = hasPermission('vehicles', 'view');
  const canExport = hasPermission('vehicles', 'export');

  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [lookups, setLookups] = useState<{ makes: Map<string, string>; types: Map<string, string> }>({
    makes: new Map(),
    types: new Map(),
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [detailRow, setDetailRow] = useState<Record<string, unknown> | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, treeRes] = await Promise.all([
        masterDataAPI.getCategory(config.category),
        ref === 'models' || ref === 'makes'
          ? masterDataAPI.getTree()
          : Promise.resolve({ data: { makes: [], types: [] } }),
      ]);

      const makes = new Map<string, string>();
      const types = new Map<string, string>();
      (treeRes.data.makes || []).forEach((m: { _id: string; name: string }) => makes.set(String(m._id), m.name));
      (treeRes.data.types || []).forEach((t: { _id: string; name: string }) => types.set(String(t._id), t.name));

      setLookups({ makes, types });
      setRows((listRes.data || []) as Record<string, unknown>[]);
    } catch {
      setError('Failed to load fleet reference data');
    } finally {
      setLoading(false);
    }
  }, [config.category, ref]);

  useEffect(() => { load(); }, [load]);

  const enrichedRows = useMemo(() => rows.map((row) => ({
    ...row,
    makeName: row.parentId ? lookups.makes.get(String(row.parentId)) || '—' : '—',
    typeName: row.vehicleTypeId ? lookups.types.get(String(row.vehicleTypeId)) || '—' : '—',
  })), [rows, lookups]);

  const allColumns = useMemo((): ModuleColumn[] => {
    const base: ModuleColumn[] = [
      { field: 'name', header: 'Name', sortable: true },
      { field: 'code', header: 'Code', sortable: true },
    ];
    if (ref === 'makes') base.push({ field: 'typeName', header: 'Vehicle Type', sortable: true });
    if (ref === 'models') {
      base.push({ field: 'makeName', header: 'Make', sortable: true });
      base.push({ field: 'typeName', header: 'Vehicle Type', sortable: true });
    }
    base.push({ field: 'status', header: 'Status', sortable: true });
    return base;
  }, [ref]);

  const tableColumns = useTableColumns(config.moduleKey, allColumns);

  const detailFields = useMemo((): ModuleField[] => {
    const fields: ModuleField[] = [
      { name: 'name', label: 'Name' },
      { name: 'code', label: 'Code' },
    ];
    if (ref === 'makes' || ref === 'models') fields.push({ name: 'typeName', label: 'Vehicle Type' });
    if (ref === 'models') fields.push({ name: 'makeName', label: 'Make' });
    fields.push({ name: 'status', label: 'Status' });
    return fields;
  }, [ref]);

  const openDetail = (row: Record<string, unknown>) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  return (
    <Box>
      <PageHeader
        title={config.title}
        subtitle={getPageSubtitle(config.moduleKey, config.title, { total: systemTotal })}
        icon={config.icon}
        isCore={CORE_MODULE_KEYS.includes(config.moduleKey)}

      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <DataTable
        columns={tableColumns.columns}
        columnPicker={{
          allColumns: tableColumns.allColumns,
          visibleFields: tableColumns.activeFields,
          defaultFields: tableColumns.defaultFields,
          onSave: tableColumns.updateColumns,
          onReset: tableColumns.resetColumns,
          isCustomized: tableColumns.isCustomized,
        }}
        rows={enrichedRows}
        loading={loading}
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onView={canView ? openDetail : undefined}
        onRowClick={canView ? openDetail : undefined}
        canView={canView}
        canExport={canExport}
        enableExcelExport={canExport}
        exportFileName={config.moduleKey}
        exportSheetName={config.title}
        searchPlaceholder="Search name or code..."
        emptyMessage="No records found"
        getRowId={(row) => String(row._id)}
        recordLabel={config.title.toLowerCase()}
      />

      <RecordDetailDrawer
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailRow(null); }}
        row={detailRow}
        fields={detailFields}
        moduleTitle={config.title.replace(/s$/, '')}
      />
    </Box>
  );
};
